import React, { useState, useEffect } from "react";
import { api } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";

const EVENT_META = {
    issue_created: { label: "Issue Created", color: "#3b82f6", icon: "+" },
    issue_actioned: { label: "Issue Actioned", color: "#f97316", icon: "\u2713" },
    issue_verified: { label: "Issue Verified", color: "#22c55e", icon: "\u2714" },
    issue_reopened: { label: "Issue Reopened", color: "#ef4444", icon: "\u21BA" },
    email_sent: { label: "Email Sent", color: "#8b5cf6", icon: "\u2709" },
    email_failed: { label: "Email Failed", color: "#ef4444", icon: "\u2709" },
    review_started: { label: "Review Started", color: "#06b6d4", icon: "\u25B6" },
    review_completed: { label: "Review Completed", color: "#22c55e", icon: "\u2611" },
    preset_locked: { label: "Preset Locked", color: "#22c55e", icon: "\uD83D\uDD12" },
};

function fmtDate(dt) {
    if (!dt) return "";
    return new Date(dt).toLocaleString("en-AU", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true
    });
}

function fmtRelative(dt) {
    if (!dt) return "";
    const diff = Date.now() - new Date(dt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return fmtDate(dt);
}

function fmtActor(actor) {
    if (!actor) return "System";
    const at = actor.indexOf("@");
    if (at > 0) {
        return actor.substring(0, at).split(/[._]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    }
    return actor;
}

function getEventMeta(type) {
    if (EVENT_META[type]) return EVENT_META[type];
    if (type && type.startsWith("rbac_")) {
        return { label: type.replace("rbac_", "").replace(/_/g, " "), color: "#eab308", icon: "\uD83D\uDD11" };
    }
    return { label: type || "Unknown", color: "#6b7280", icon: "\u2022" };
}

const EVENT_TYPES = [
    { value: "", label: "All Events" },
    { value: "issue_created", label: "Issue Created" },
    { value: "issue_actioned", label: "Issue Actioned" },
    { value: "issue_verified", label: "Issue Verified" },
    { value: "issue_reopened", label: "Issue Reopened" },
    { value: "email_sent", label: "Email Sent" },
    { value: "email_failed", label: "Email Failed" },
    { value: "review_completed", label: "Review Completed" },
    { value: "preset_locked", label: "Preset Locked" },
];

function AuditLog() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [typeFilter, setTypeFilter] = useState("");
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 100;

    useEffect(() => {
        setLoading(true);
        const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
        if (typeFilter) params.type = typeFilter;
        api.activityFeed(params)
            .then(data => {
                setEvents(data.events || []);
                setError(null);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [typeFilter, page]);

    return (
        <div>
            <div className="page-header">
                <h2>Activity Log</h2>
                <p style={{ color: "var(--text-secondary)" }}>
                    Combined timeline of issue actions, emails, reviews, and system events.
                </p>
            </div>

            <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
                <select
                    className="filter-select"
                    value={typeFilter}
                    onChange={e => { setTypeFilter(e.target.value); setPage(0); }}
                    style={{ minWidth: 180 }}
                >
                    {EVENT_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                </select>
                {page > 0 && (
                    <button className="preset-action-btn" onClick={() => setPage(p => p - 1)}>
                        &larr; Newer
                    </button>
                )}
                {events.length === PAGE_SIZE && (
                    <button className="preset-action-btn" onClick={() => setPage(p => p + 1)}>
                        Older &rarr;
                    </button>
                )}
                <span style={{ fontSize: 12, color: "var(--text-muted)", marginLeft: "auto" }}>
                    Page {page + 1}
                </span>
            </div>

            {loading && <LoadingSpinner message="Loading activity..." />}

            {error && (
                <div className="empty-state">
                    <h3>Failed to load activity</h3>
                    <p>{error}</p>
                </div>
            )}

            {!loading && !error && events.length === 0 && (
                <div className="empty-state">
                    <h3>No events found</h3>
                    <p>{typeFilter ? "No events of this type." : "No activity recorded yet."}</p>
                </div>
            )}

            {!loading && !error && events.length > 0 && (
                <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: "var(--bg-secondary)", textAlign: "left" }}>
                                <th style={{ padding: "8px 12px", fontWeight: 600, color: "var(--text-secondary)", width: 150 }}>Time</th>
                                <th style={{ padding: "8px 12px", fontWeight: 600, color: "var(--text-secondary)", width: 140 }}>Event</th>
                                <th style={{ padding: "8px 12px", fontWeight: 600, color: "var(--text-secondary)", width: 140 }}>Actor</th>
                                <th style={{ padding: "8px 12px", fontWeight: 600, color: "var(--text-secondary)" }}>Detail</th>
                                <th style={{ padding: "8px 12px", fontWeight: 600, color: "var(--text-secondary)", width: 130 }}>Store</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((ev, i) => {
                                const meta = getEventMeta(ev.type);
                                return (
                                    <tr key={i} style={{ borderTop: "1px solid var(--border)" }}>
                                        <td style={{ padding: "6px 12px", color: "var(--text-muted)", fontSize: 12 }}
                                            title={fmtDate(ev.at)}>
                                            {fmtRelative(ev.at)}
                                        </td>
                                        <td style={{ padding: "6px 12px" }}>
                                            <span style={{
                                                display: "inline-block", padding: "2px 8px",
                                                borderRadius: 4, fontSize: 11, fontWeight: 600,
                                                background: meta.color + "18", color: meta.color
                                            }}>
                                                {meta.icon} {meta.label}
                                            </span>
                                        </td>
                                        <td style={{ padding: "6px 12px", color: "var(--text-secondary)" }}>
                                            {fmtActor(ev.actor)}
                                        </td>
                                        <td style={{ padding: "6px 12px", color: "var(--text-primary)", maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {ev.detail}
                                            {ev.severity && (
                                                <span className={`severity ${ev.severity}`} style={{ marginLeft: 6, fontSize: 10 }}>
                                                    {ev.severity}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: "6px 12px", fontSize: 12, color: "var(--text-muted)" }}>
                                            {ev.storeName ? `${ev.storeName}` : ""}
                                            {ev.siteId && !ev.storeName ? `#${ev.siteId}` : ""}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default AuditLog;
